import type { ClientGetOneAPI } from '@hero/api';
import { Client, getObjectId, Id } from '@hero/schema';
import { useApi } from '@hero/context';
import type {
  UseClientApi,
  UseClientCreateApi,
  UseClientDeleteApi,
  UseClientGetByIdsApi,
  UseClientListApi,
  UseClientLookupByLinkApi,
  UseClientUpdateApi,
} from './types';
import { useInfiniteQueryAPI, useMutationAPI, useQueryAPI } from '../../utils';

/**
 * Get single client by ID. Pass id for normal use; pass undefined to
 * get runQuery only (e.g. in selectors). When id is undefined, the
 * query is disabled until you call runQuery({ _id }) to fetch; then
 * data/isLoading reflect that id.
 *
 * Hook API: {@link UseClientApi}
 */
export const useClientApi: UseClientApi = (id, apiConfig) => {
  const api = useApi();
  const _id = id != null ? getObjectId(id) : undefined;
  return useQueryAPI({
    entity: 'client',
    apiFn: api.clients.getClientById,
    query: { _id } as ClientGetOneAPI['req'],
    apiConfig,
    isEnabled: () => !!id,
  });
};

/**
 * List clients with pagination (skip/limit).
 *
 * Hook API: {@link UseClientListApi}
 */
export const useClientListApi: UseClientListApi = (query, apiConfig) => {
  const api = useApi();
  return useInfiniteQueryAPI({
    entity: 'client',
    apiFn: api.clients.getClients,
    query,
    apiConfig,
    isEnabled: () => true,
  });
};

/**
 * Create client. Handles cache invalidation and optional onSuccess/onError.
 *
 * Hook API: {@link UseClientCreateApi}
 */
export const useClientCreateApi: UseClientCreateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'client',
    apiFn: api.clients.createClient,
    apiConfig,
  });
};

/**
 * Update client. Handles cache invalidation and optional onSuccess/onError.
 * API from ApiProvider; pass apiConfig to override apiFn, etc.
 *
 * Hook API: {@link UseClientUpdateApi}
 */
export const useClientUpdateApi: UseClientUpdateApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'client',
    apiFn: api.clients.updateClient,
    apiConfig,
  });
};

/**
 * Delete client. Handles cache invalidation and optional onSuccess/onError.
 *
 * Hook API: {@link UseClientDeleteApi}
 */
export const useClientDeleteApi: UseClientDeleteApi = apiConfig => {
  const api = useApi();
  return useMutationAPI({
    entity: 'client',
    apiFn: api.clients.deleteClient,
    apiConfig,
  });
};

export const useClientGetByIdsApi: UseClientGetByIdsApi = apiConfig => {
  const api = useApi();

  const apiFn = async (targetIds: Id[]) => {
    const ids = targetIds.map(getObjectId);
    const result = ids.map(id => api.clients.getClientById({ _id: id }) as Promise<Client>);
    return Promise.all(result);
  };

  return useMutationAPI({
    entity: 'client',
    apiFn,
    apiConfig,
  });
};

/**
 * Own-tenant dedup probe used by the Client-create picker. Disabled
 * until at least one of `profileId` / `orgId` is set; returns
 * `null` (not `undefined`) when no Client is linked to that
 * identity in the caller's Org.
 *
 * Hook API: {@link UseClientLookupByLinkApi}
 */
export const useClientLookupByLinkApi: UseClientLookupByLinkApi = (query, apiConfig) => {
  const api = useApi();
  return useQueryAPI({
    entity: 'client',
    apiFn: api.clients.lookupByLink,
    query,
    apiConfig,
    isEnabled: () => Boolean(query.profileId) || Boolean(query.orgId),
  });
};
